<template>
  <div class="multi-store-drawer-trigger">
    <transition name="fade">
      <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
      <img
        v-if="flag"
        :key="`flag-${currentCountryCode}`"
        alt=""
        class="multi-store-drawer-trigger__flag"
        role="presentation"
        :src="flag.src"
        :srcset="flag.srcset"
      >
    </transition>

    <div class="multi-store-drawer-trigger__buttons">
      <btn
        class="multi-store-drawer-trigger__button"
        :label="countryText"
        modifiers="text"
        type="button"
        @click="openMultiStoreDrawer"
      />

      <btn
        class="multi-store-drawer-trigger__button"
        :label="languageText"
        modifiers="text"
        type="button"
        @click="openMultiStoreDrawer"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Multi store drawer trigger (multi-store-drawer-trigger)
 * -----------------------------------------------------------------------------
 * Component that displays the current store's flag, name and language which
 * controls opening the multi store drawer.
 *
 * @param {String} namespace - Namespace of the overlay this element controls.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'

export default {
  name: 'MultiStoreDrawerTrigger',

  components: {
    Btn,
  },

  props: {
    namespace: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      currentCountryCode: '',
      currentLanguageCode: '',
      isExpanded: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      current: (state) => state['multi-store'].current,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute country display text.
     * @returns {String}
     */
    countryText() {
      return this.isMobile
        ? this.$variable('store.countryName')
        : this.$string('general.multi_store.drawer.country_trigger')
          .replace('{{ country_name }}', this.$variable('store.countryName'))
    },

    /**
     * Compute flag URL.
     * @returns {Object}
     */
    flag() {
      const code = this.current.country.code?.toLowerCase()

      if (!code) {
        return false
      }

      return {
        src: `https://flagcdn.com/w40/${code}.png`,
        srcset: `https://flagcdn.com/w40/${code}.png 40w, https://flagcdn.com/w80/${code}.png 80w, https://flagcdn.com/w160/${code}.png 160w`,
      }
    },

    /**
     * Compute language display text.
     * @returns {String}
     */
    languageText() {
      return this.isMobile
        ? this.$variable('store.languageName')
        : this.$string('general.multi_store.drawer.language_trigger')
          .replace('{{ language_name }}', this.$variable('store.languageName'))
    },
  },

  watch: {

    /**
     * Watch active overlay set aria expanded values.
     */
    getActiveOverlay() {
      this.isExpanded = this.getActiveOverlay?.namespace === this.namespace
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Opens the multi store drawer.
     */
    openMultiStoreDrawer() {
      this.openOverlay({
        component: 'multi-store-drawer',
        ignoreDismissed: true,
        namespace: 'multiStoreDrawer',
      })
    },
  },
}
</script>

<style lang="scss">
@import './multi-store-drawer-trigger';
</style>
